// packages/studio/web/src/repos/browser/RepoBrowserPage.tsx
//
// Repo file browser page.
// Route: /:slug/:repo/_/files/:ref/*
//
// Layout:
//   top toolbar  — BranchSwitcher
//   left rail    — FileTree (260 px)
//   right pane   — FilePreview (fills remainder)

import { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { BranchSwitcher } from "./BranchSwitcher.js";
import { FileTree } from "./FileTree.js";
import { FilePreview } from "./FilePreview.js";
import { NewFileModal } from "./NewFileModal.js";
import { Button } from "../../ui/Button.js";
import { useContextMenu } from "../../ui/ContextMenu";
import { repoFiles } from "../../lib/paths";
import { useTranslation } from "../../i18n";

export function RepoBrowserPage() {
  const { slug = "", repo = "", ref = "main", "*": splat = "" } = useParams() as Record<string, string>;
  const path = splat;
  const nav = useNavigate();
  const { t } = useTranslation("repos");
  const menu = useContextMenu();
  const [newFileOpen, setNewFileOpen] = useState(false);

  return (
    <div className="grid h-full grid-rows-[auto_1fr]">
      <div className="flex items-center gap-2 border-b border-slate-800 bg-slate-950 px-3 py-2">
        <BranchSwitcher
          wsSlug={slug}
          repoSlug={repo}
          currentRef={ref}
          onChange={(b) => nav(repoFiles(slug, repo, b, path))}
        />
        {path ? (
          <span
            className="truncate font-mono text-xs text-slate-500"
            onContextMenu={(e) =>
              menu.open(e, [
                {
                  label: t("menu.copyPath"),
                  onClick: () => void navigator.clipboard?.writeText(path),
                },
                {
                  label: t("menu.copyFilename"),
                  onClick: () =>
                    void navigator.clipboard?.writeText(path.split("/").pop() ?? path),
                },
              ])
            }
          >
            {path}
          </span>
        ) : null}
        <Button
          variant="ghost"
          size="sm"
          className="ml-auto"
          onClick={() => setNewFileOpen(true)}
        >
          + New file
        </Button>
      </div>
      <NewFileModal
        open={newFileOpen}
        onClose={() => setNewFileOpen(false)}
        wsSlug={slug}
        repoSlug={repo}
        ref={ref}
      />
      <div className="grid min-h-0 grid-cols-[16rem_1fr] overflow-hidden">
        <div className="overflow-y-auto border-r border-slate-800 bg-slate-950/40">
          <FileTree wsSlug={slug} repoSlug={repo} ref={ref} selectedPath={path} />
        </div>
        <div className="overflow-y-auto bg-[radial-gradient(50rem_30rem_at_70%_-10%,rgba(56,189,248,0.04),transparent)]">
          <FilePreview wsSlug={slug} repoSlug={repo} ref={ref} path={path} />
        </div>
      </div>
      {menu.node}
    </div>
  );
}
